import { DateRangePickerPresetKey } from '@shigo-ui/ui';
import { analyticsPresetRange, normalizeAnalyticsRange } from '@/lib/analyticsDateRange';

import type { ReportExplorerConfiguration, ReportFilter, ReportRequest } from './model';
import { ReportDateRangeType, ReportDimension, ReportTimeGranularity } from './reportEnums';
import { deserializeReportExplorerFilters } from './reportFilters';
import { identityMetricsForDimensions } from './reportRequest';

const granularityByDimensions = new Map<string, ReportTimeGranularity>([
  [ReportDimension.Day, ReportTimeGranularity.Day],
  [[ReportDimension.Day, ReportDimension.Hour].join(','), ReportTimeGranularity.Hour],
  [[ReportDimension.Year, ReportDimension.Month].join(','), ReportTimeGranularity.Month],
  [ReportDimension.Year, ReportTimeGranularity.Year],
]);

export const datePresetKeyByReportDateRange: Record<ReportDateRangeType, DateRangePickerPresetKey> =
  {
    [ReportDateRangeType.Today]: DateRangePickerPresetKey.Today,
    [ReportDateRangeType.Yesterday]: DateRangePickerPresetKey.Yesterday,
    [ReportDateRangeType.ThisWeek]: DateRangePickerPresetKey.ThisWeek,
    [ReportDateRangeType.Last7Days]: DateRangePickerPresetKey.Last7Days,
    [ReportDateRangeType.ThisMonth]: DateRangePickerPresetKey.ThisMonth,
    [ReportDateRangeType.Last30Days]: DateRangePickerPresetKey.Last30Days,
    [ReportDateRangeType.LastMonth]: DateRangePickerPresetKey.LastMonth,
    [ReportDateRangeType.Last3Months]: DateRangePickerPresetKey.Last3Months,
    [ReportDateRangeType.Last6Months]: DateRangePickerPresetKey.Last6Months,
    [ReportDateRangeType.ThisYear]: DateRangePickerPresetKey.ThisYear,
  };

interface ReportConfigurationOptions {
  resolveRelativeDateRange?: boolean;
  now?: Date;
  filterValueLabels?: Readonly<Record<string, readonly string[]>>;
}

export function reportRequestsToExplorerConfiguration(
  report: ReportRequest,
  chartReports: readonly ReportRequest[],
  options: ReportConfigurationOptions = {},
): ReportExplorerConfiguration {
  const chart = chartReports[0] ?? report;
  const { reportFilters, passthroughFilters } = deserializeReportExplorerFilters(
    report.filters,
    options.filterValueLabels,
  );
  const range = resolveReportRange(report.filters, options);

  return {
    ...range,
    tableDimensions: report.dimensions,
    tableMetrics: withoutIdentityMetrics(report.metrics, report.dimensions),
    chartGranularity:
      granularityByDimensions.get(chart.dimensions.join(',')) ?? ReportTimeGranularity.Day,
    chartMetrics: withoutIdentityMetrics(chart.metrics, chart.dimensions),
    timeShiftDays: readTimeShiftDays(chart.filters, chartReports[1]?.filters),
    reportFilters,
    passthroughFilters,
  };
}

function resolveReportRange(filters: ReportFilter, options: ReportConfigurationOptions) {
  const preset = filters.dateRange ?? null;
  if (options.resolveRelativeDateRange && preset) {
    const value = analyticsPresetRange(datePresetKeyByReportDateRange[preset], options.now);
    return normalizeReportExplorerRange(
      {
        dateFrom: value.dateFrom,
        dateTo: value.dateTo,
        dateRange: Object.values(ReportDateRangeType).find((key) => key === value.preset) ?? null,
      },
      options.now,
    );
  }
  return normalizeReportExplorerRange(
    { dateFrom: filters.dateFrom, dateTo: filters.dateTo, dateRange: preset },
    options.now,
  );
}

function withoutIdentityMetrics<TMetric>(
  metrics: readonly TMetric[],
  dimensions: readonly ReportDimension[],
) {
  const identity = new Set(identityMetricsForDimensions(dimensions));
  return metrics.filter((metric) => !identity.has(metric as never));
}

function readTimeShiftDays(current: ReportFilter, shifted: ReportFilter | undefined) {
  if (!shifted) return null;
  const currentFrom = parseIsoDate(current.dateFrom);
  const shiftedFrom = parseIsoDate(shifted.dateFrom);
  if (currentFrom === null || shiftedFrom === null) return null;
  const days = Math.round((currentFrom - shiftedFrom) / 86_400_000);
  return Number.isSafeInteger(days) && days > 0 ? days : null;
}

function parseIsoDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function normalizeReportExplorerRange<
  T extends Pick<ReportExplorerConfiguration, 'dateFrom' | 'dateTo' | 'dateRange'>,
>(configuration: T, now = new Date()): T {
  const range = normalizeAnalyticsRange(
    {
      dateFrom: configuration.dateFrom,
      dateTo: configuration.dateTo,
      preset: configuration.dateRange ?? undefined,
    },
    now,
  );
  return {
    ...configuration,
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    dateRange: Object.values(ReportDateRangeType).find((preset) => preset === range.preset) ?? null,
  };
}
